import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useNavigate, useParams } from "react-router-dom";

import { normalizeErrorMessages } from "api/common/utils/rtkq.utils";
import {
  useDeleteMailListMutation,
  useGetMailListQuery,
} from "api/memberPortal/mailList/mailList.api";
import LoadingSpinner from "components/UI/LoadingSpinner/LoadingSpinner";

export default function DeleteMailListModal() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: mailList, isLoading: isLoadingList } = useGetMailListQuery(
    id ?? skipToken,
  );

  const [deleteMailList, { isLoading: isDeleting, error }] =
    useDeleteMailListMutation();

  const handleClose = () => {
    navigate("..");
  };

  const handleDelete = async () => {
    if (!id) return;
    const result = await deleteMailList(id);

    if (!("error" in result)) {
      navigate("/community/mailing-lists", {
        replace: true,
        state: { deletedListName: mailList?.name },
      });
    }
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <DeleteOutlineIcon color="error" />
        Delete Mailing List
      </DialogTitle>

      <DialogContent>
        {isLoadingList ? (
          <LoadingSpinner />
        ) : (
          <Box>
            <Typography variant="body1">
              Are you sure you want to delete this mailing list?
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, fontWeight: "bold" }}>
              This action cannot be undone.
            </Typography>

            {mailList && (
              <Card
                sx={{
                  p: 2,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  {mailList.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {mailList.email}
                </Typography>
                {mailList.memberCount > 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {mailList.memberCount} member
                    {mailList.memberCount !== 1 ? "s" : ""} will be removed
                  </Typography>
                )}
              </Card>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mr: "auto" }}>
            {normalizeErrorMessages(error)}
          </Alert>
        )}
        <Button variant="outlined" onClick={handleClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={isDeleting || isLoadingList}
          loading={isDeleting}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}
